/**
 * Share API Service
 * API client for sharing analysis with external recipients (double opt-in flow)
 */

import { apiClient } from './client';

/**
 * Request payload for sharing an analysis
 */
export interface ShareAnalysisRequest {
  correlationId: string;
  recipientEmail: string;
  recipientFirstName?: string;
  recipientLastName?: string;
  personalMessage?: string;
}

/**
 * Share status enum matching backend
 */
export enum AnalysisShareStatus {
  PendingOptIn = 0,
  OptInConfirmed = 1,
  Viewed = 2,
  Expired = 3,
  Revoked = 4
}

/**
 * Response from the share analysis endpoint
 */
export interface ShareAnalysisResponse {
  id: string;
  correlationId: string;
  recipientEmail: string;
  recipientFirstName?: string | null;
  recipientLastName?: string | null;
  personalMessage?: string;
  status: AnalysisShareStatus;
  optInConfirmedAt?: string;
  firstViewedAt?: string;
  viewCount: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * Response from opt-in confirmation
 */
export interface ConfirmOptInResponse {
  success: boolean;
  message: string;
  senderName?: string;
}

/**
 * Response from the shared analysis endpoint (public)
 */
export interface SharedAnalysisData {
  shareId: string;
  senderName: string;
  personalMessage?: string;
  sharedAt: string;
  expiresAt: string;
  correlationId: string;
  productName: string;
  productId: number;
  resultJSON: string;
  analysisCreatedAt: string;
  analysisFinishedAt?: string;
}

/**
 * Share service API methods
 */
export const shareService = {
  /**
   * Share an analysis with an external recipient
   * Creates a share record and sends opt-in request email (Email #1)
   */
  async shareAnalysis(data: ShareAnalysisRequest): Promise<ShareAnalysisResponse> {
    return apiClient.post<ShareAnalysisResponse>('/api/Share', data);
  },

  /**
   * Confirm opt-in (public endpoint)
   * Validates the opt-in token and sends share link email (Email #2)
   */
  async confirmOptIn(token: string): Promise<ConfirmOptInResponse> {
    return apiClient.get<ConfirmOptInResponse>(`/api/Share/confirm?token=${encodeURIComponent(token)}`);
  },

  /**
   * Get shared analysis data (public endpoint)
   * Used by recipients who have confirmed opt-in
   */
  async getSharedAnalysis(shareToken: string): Promise<SharedAnalysisData> {
    return apiClient.get<SharedAnalysisData>(`/api/shared/${shareToken}`);
  },

  /**
   * Get all shares created by the current user
   */
  async getMyShares(): Promise<ShareAnalysisResponse[]> {
    return apiClient.get<ShareAnalysisResponse[]>('/api/Share/my-shares');
  },

  /**
   * Get a specific share by ID
   */
  async getShareById(shareId: string): Promise<ShareAnalysisResponse> {
    return apiClient.get<ShareAnalysisResponse>(`/api/Share/${shareId}`);
  },

  /**
   * Revoke an existing share
   */
  async revokeShare(shareId: string): Promise<{ message: string }> {
    return apiClient.delete<{ message: string }>(`/api/Share/${shareId}`);
  },
};
